import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { GeneralFileNodeType } from "@/enums/DocumentCenterFileType";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { useResponsive } from "@/hooks/useResponsive";
import { DocumentCenterNode, DocumentCenterNodeUtils } from "@/models/DocumentCenterNode";
import DataRoomService from "@/services/DataRoomService";
import DocumentCenterFileService from "@/services/DocumentCenterFileService";
import { Box, SxProps, Typography, styled } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { StyledInputField } from "../fundraising/advanced-search/AdvancedSearchFilters";
import CommonButton from "../utils/CommonButton";
import LazoModal from "../utils/LazoModal";
import DocumentCenterTree from "./DocumentCenterTree";

type Props = {
  open: boolean;
  setOpen: (b: boolean) => void;
};

const debouncer = new Debouncer(150);

function DataRoomDocumentCenterImportModal(props: Props) {
  const { t } = useTranslation("virtualDataRooms");
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;
  const [tree, setTree] = useState<DocumentCenterNode>(null);
  const [importing, setImporting] = useState<boolean>(false);
  const [searchMatches, setSearchMatches] = useState<null | Set<DocumentCenterNode>>(new Set());

  function handleCloseClick() {
    props.setOpen(false);
  }

  const { currentTenantMembership } = useTenantMemberships();

  useEffect(() => {
    DocumentCenterFileService.getTree(currentTenantMembership.tenant.externalId).then((r) => {
      r.onSuccess((c) => {
        const t = {
          name: "root",
          externalId: "root-node",
          type: GeneralFileNodeType.FOLDER,
          children: c,
        };
        setTree(DocumentCenterNodeUtils.treeFilterEmpty(t));
      });
      r.onError((e) => {
        console.log("Error: " + e);
      });
    });
  }, [currentTenantMembership.tenant.externalId]);

  function handleImport() {
    const externalIds = DocumentCenterNodeUtils.extractFilesFromSelected(tree);
    setImporting(true);
    DataRoomService.importFilesDocumentCenter(externalIds).then((response) => {
      setImporting(false);
      response.onSuccess(() => {
        props.setOpen(false);
        NotificationService.info(t("documentCenterImport.notifications.importInProgress"));
      });
      response.onError((errorMessage) => {
        NotificationService.error(errorMessage);
      });
    });
  }

  function onSearch(inputSearch: string) {
    debouncer.executeLastCall(() => {
      if (inputSearch !== "") {
        let matches: Set<DocumentCenterNode> = new Set();
        DocumentCenterNodeUtils.treeFindName(matches, inputSearch, tree);
        setSearchMatches(matches);
      } else {
        setSearchMatches(null);
      }
    });
  }

  const selectedFileCount = DocumentCenterNodeUtils.treeCountSelectedFiles(tree);

  const location = useLocation();

  return (
    <>
      <LazoModal
        open={props.open}
        title={t("documentCenterImport.modal.title")}
        handleClose={handleCloseClick}
        sx={{
          ...ModalSx,
          "&.modal-container": {
            width: isMobile ? "95vw" : isTablet ? "70vw" : "30vw",
            maxWidth: isMobile ? "95vw" : isTablet ? "70vw" : "30vw",
            height: isMobile ? "90vh" : "auto",
            maxHeight: isMobile ? "90vh" : "80vh",
          },
        }}
        buttons={[
          <CommonButton
            style={{
              borderRadius: "0.5rem",
              minWidth: isMobile ? "100px" : "auto",
              fontSize: isMobile ? "0.875rem" : "1rem",
            }}
            purpleBorderVariant
            onClick={handleCloseClick}
            key={"cancel-button"}
          >
            {t("documentCenterImport.modal.buttons.cancel")}
          </CommonButton>,
          <CommonButton
            disabled={importing || DocumentCenterNodeUtils.treeCountSelected(tree) === 0}
            style={{
              borderRadius: "0.5rem",
              minWidth: isMobile ? "100px" : "auto",
              fontSize: isMobile ? "0.875rem" : "1rem",
            }}
            onClick={handleImport}
            key={"import-button"}
          >
            {importing ? <Spinner size={isMobile ? "1.25rem" : "1.5rem"} /> : t("documentCenterImport.modal.buttons.import")}
          </CommonButton>,
        ]}
      >
        <Box className="main-container">
          <Box className="top-container">
            <StyledInputField
              onChange={(v) => onSearch(v.currentTarget.value)}
              placeholder={t("documentCenterImport.search.placeholder")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: isMobile ? "2.5rem" : "2.25rem",
                  width: "100%",
                  fontSize: isMobile ? "0.875rem" : "1rem",
                },
                width: "100%",
              }}
            />
            <Box width="100%">
              <Box
                display="flex"
                alignItems={isMobile ? "flex-start" : "center"}
                justifyContent="space-between"
                flexDirection={isMobile ? "column" : "row"}
                gap={isMobile ? "0.5rem" : 0}
                width="100%"
              >
                <Typography fontSize={isMobile ? "0.75rem" : "0.875rem"} sx={{ lineHeight: isMobile ? 1.3 : 1.5 }}>
                  {t("documentCenterImport.fileSelection.instruction")}
                </Typography>
                <Pill sx={{ alignSelf: isMobile ? "flex-start" : "center" }}>
                  <Typography fontSize={isMobile ? "0.625rem" : "0.75rem"} fontWeight="400" lineHeight={isMobile ? "14px" : "18px"}>
                    {selectedFileCount === 0
                      ? t("documentCenterImport.fileSelection.noFilesSelected")
                      : t("documentCenterImport.fileSelection.filesSelected", { count: selectedFileCount })}
                  </Typography>
                </Pill>
              </Box>
            </Box>
          </Box>
          <Box className="tree-container">
            <DocumentCenterTree matches={searchMatches} tree={tree} setTree={(t) => setTree(t)} level={0} />
          </Box>
        </Box>
        <Box className="footer" sx={{ padding: isMobile ? "0.5rem 0" : 0 }}>
          {!location.pathname.includes("/virtual-data-rooms") && (
            <Typography
              className="hint-typography"
              sx={{
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                lineHeight: isMobile ? 1.3 : 1.5,
                textAlign: isMobile ? "center" : "left",
              }}
            >
              {t("documentCenterImport.processingHint")}
            </Typography>
          )}
        </Box>
      </LazoModal>
    </>
  );
}

const ModalSx: SxProps = {
  "& .title-container": {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    "& .MuiTypography-root": {
      fontWeight: "600",
      fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
    },
  },
  "&.modal-container": {
    width: "30vw",
    maxWidth: "30vw",
  },
  "& .main-container": {
    display: "flex",
    flexDirection: "column",
    gap: { xs: "0.25rem", sm: "0.375rem", md: "0.5rem" },
    justifyContent: "flex-start",
    position: "relative",
    "& .tree-container": {
      height: { xs: "35vh", sm: "45vh", md: "50vh" },
      maxHeight: { xs: "35vh", sm: "45vh", md: "50vh" },
      overflow: "auto",
      overflowX: "hidden",
      borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    },
    "& .top-container": {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "center",
      gap: { xs: "0.25rem", sm: "0.375rem", md: "0.5rem" },
      paddingBottom: { xs: "0.5rem", sm: "0.625rem", md: "0.75rem" },
      borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    },
  },
  "& .footer": {
    "& .hint-typography": {
      fontSize: "0.875rem",
      color: Colors.BLACK_2,
    },
  },
};

const Pill = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  borderRadius: "100px",
  border: `1px solid ${Colors.BUTTON_SECONDARY_HOVER_PURPLE}`,
  padding: "0.125rem 0.5rem",
}));

export default DataRoomDocumentCenterImportModal;
